import React, { useState } from 'react';
import { X, Mail, CheckCircle, Sparkles, Heart, MessageCircle, AlertCircle, ArrowRight, Loader2, Bell } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

export const AuthModal: React.FC = () => {
  const { 
    isAuthModalOpen, 
    closeAuthModal, 
    authModalAction, 
    signInWithGoogle, 
    subscribeAndSignInWithEmail,
    loginExistingSubscriber
  } = useAuth();

  const [mode, setMode] = useState<'subscribe' | 'login'>('subscribe');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  if (!isAuthModalOpen) return null;

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError(null);
    try {
      await signInWithGoogle();
    } catch (err: any) {
      console.error(err);
      setError('Não foi possível conectar com o Google no momento. Você também pode se cadastrar informando seu e-mail abaixo.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      setError('Por favor, digite seu e-mail.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (mode === 'subscribe') {
        const result = await subscribeAndSignInWithEmail(email, name);
        if (result.success) {
          setSuccessMsg('Inscrição confirmada com sucesso! Você agora é membro da comunidade.');
          setTimeout(() => {
            closeAuthModal();
          }, 1500);
        } else {
          setError(result.error || 'Erro ao realizar inscrição.');
        }
      } else {
        const result = await loginExistingSubscriber(email);
        if (result.success) {
          setSuccessMsg('Bem-vindo(a) de volta! Sessão iniciada.');
          setTimeout(() => {
            closeAuthModal();
          }, 1500);
        } else {
          setError(result.error || 'Não encontramos sua inscrição com esse e-mail.');
        }
      }
    } catch (err: any) {
      setError('Ocorreu um erro. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="fixed inset-0 z-[120] flex items-center justify-center p-3 sm:p-4 md:p-6 bg-black/85 backdrop-blur-md animate-fade-in overflow-y-auto"
      onClick={closeAuthModal}
    >
      <div 
        className="bg-[#0e0e0e] border border-stone-800 w-full max-w-[94vw] sm:max-w-md max-h-[92vh] overflow-y-auto rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 shadow-[0_20px_60px_rgba(0,0,0,0.9)] relative text-stone-200 hide-scrollbar my-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Glow decoration */}
        <div className="absolute top-0 right-0 w-40 sm:w-48 h-40 sm:h-48 bg-primary/10 rounded-full blur-3xl pointer-events-none -mr-16 -mt-16" />
        <div className="absolute bottom-0 left-0 w-40 sm:w-48 h-40 sm:h-48 bg-rose-950/20 rounded-full blur-3xl pointer-events-none -ml-16 -mb-16" />

        {/* Close Button */}
        <button 
          onClick={closeAuthModal}
          className="absolute top-3 right-3 sm:top-5 sm:right-5 p-1.5 sm:p-2 rounded-full text-stone-400 hover:text-white bg-stone-900/80 hover:bg-stone-800 transition-colors z-10"
          title="Fechar"
        >
          <X size={16} className="sm:w-[18px] sm:h-[18px]" />
        </button>

        {/* Header Badge */}
        <div className="flex items-center gap-2 mb-2 sm:mb-4">
          <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-primary/20 text-primary flex items-center justify-center border border-primary/30 shadow-[0_0_12px_rgba(193,18,31,0.3)] shrink-0">
            <Sparkles size={14} className="sm:w-4 sm:h-4" />
          </div>
          <span className="text-[10px] sm:text-[11px] font-sans font-bold uppercase tracking-wider text-primary">
            Comunidade Literafan
          </span>
        </div>

        {/* Title */}
        <h2 className="font-serif text-xl sm:text-2xl md:text-3xl font-bold text-white mb-1.5 sm:mb-2 leading-snug">
          {mode === 'subscribe' ? 'Inscreva-se para Interagir' : 'Bem-vindo de Volta'}
        </h2>

        {/* Dynamic Context Message */}
        <p className="text-stone-400 text-xs sm:text-sm leading-relaxed mb-3 sm:mb-5 font-sans">
          {authModalAction ? (
            <span>
              Para <strong className="text-stone-200">{authModalAction}</strong>, você só precisa se inscrever gratuitamente em nossa Newsletter.
            </span>
          ) : (
            'Ao se inscrever na newsletter, você recebe notificações de novas publicações em primeira mão, além de poder curtir e comentar livremente.'
          )}
        </p>

        {/* Features Pill */}
        <div className="space-y-1.5 sm:space-y-2 p-2.5 sm:p-3.5 bg-stone-900/70 border border-stone-800/80 rounded-xl sm:rounded-2xl mb-3 sm:mb-5 text-xs text-stone-300 font-sans">
          <div className="flex items-center gap-2 text-stone-200">
            <div className="w-5 h-5 rounded-full bg-emerald-950/80 border border-emerald-600/40 text-emerald-400 flex items-center justify-center flex-shrink-0">
              <Bell size={11} className="animate-pulse" />
            </div>
            <span className="font-medium text-[11px] sm:text-xs">
              Notificação de novas publicações <span className="text-[9px] sm:text-[10px] text-emerald-400 font-bold uppercase">(Ativo)</span>
            </span>
          </div>
          <div className="grid grid-cols-2 gap-1.5 sm:gap-2 pt-1.5 sm:pt-2 border-t border-stone-800/60 text-[10px] sm:text-[11px] text-stone-400">
            <div className="flex items-center gap-1.5">
              <Heart size={11} className="text-red-500 fill-current flex-shrink-0" />
              <span>Curtir artigos & fanarts</span>
            </div>
            <div className="flex items-center gap-1.5">
              <MessageCircle size={11} className="text-stone-300 flex-shrink-0" />
              <span>Comentar no mural</span>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-3 sm:mb-5 p-2.5 sm:p-3.5 bg-red-950/40 border border-red-800/60 rounded-xl text-red-300 text-xs flex items-start gap-2">
            <AlertCircle size={15} className="text-red-400 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {successMsg && (
          <div className="mb-3 sm:mb-5 p-2.5 sm:p-3.5 bg-emerald-950/40 border border-emerald-800/60 rounded-xl text-emerald-300 text-xs flex items-center gap-2">
            <CheckCircle size={15} className="text-emerald-400 flex-shrink-0" />
            <span>{successMsg}</span>
          </div>
        )}

        {/* Google 1-Click Button */}
        <button
          type="button"
          onClick={handleGoogleSignIn}
          disabled={loading}
          className="w-full flex items-center justify-center gap-2.5 py-2.5 sm:py-3 px-3 sm:px-4 bg-white hover:bg-stone-100 text-stone-900 font-sans font-bold text-xs rounded-full shadow-lg transition-all duration-200 hover:scale-[1.01] active:scale-[0.99] disabled:opacity-50 mb-3 sm:mb-4"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24">
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
            />
          </svg>
          <span>Continuar com Google</span>
        </button>

        {/* Divider */}
        <div className="flex items-center gap-3 my-2.5 sm:my-4">
          <div className="h-px bg-stone-800 flex-grow" />
          <span className="text-[9px] sm:text-[10px] font-sans uppercase tracking-widest text-stone-500">ou com seu e-mail</span>
          <div className="h-px bg-stone-800 flex-grow" />
        </div>

        {/* Email Form */}
        <form onSubmit={handleSubmitEmail} className="space-y-2.5 sm:space-y-3 font-sans">
          {mode === 'subscribe' && (
            <div>
              <label className="block text-[10px] sm:text-[11px] font-medium text-stone-400 mb-1">
                Seu Nome (como aparecerá nos comentários)
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex: Amanda Silva"
                className="w-full px-3.5 sm:px-4 py-2 sm:py-2.5 bg-black/50 border border-stone-800 rounded-xl text-stone-200 text-xs sm:text-sm focus:outline-none focus:border-primary transition-colors placeholder:text-stone-600"
              />
            </div>
          )}

          <div>
            <label className="block text-[10px] sm:text-[11px] font-medium text-stone-400 mb-1">
              Seu E-mail da Newsletter
            </label>
            <div className="relative">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="seu-email@exemplo.com"
                className="w-full px-3.5 sm:px-4 py-2 sm:py-2.5 bg-black/50 border border-stone-800 rounded-xl text-stone-200 text-xs sm:text-sm focus:outline-none focus:border-primary transition-colors placeholder:text-stone-600 pr-9 sm:pr-10"
              />
              <Mail size={15} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-500 pointer-events-none" />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-2.5 sm:py-3 px-3 sm:px-4 bg-primary hover:bg-red-700 text-white font-sans font-bold text-xs rounded-full shadow-lg shadow-primary/20 transition-all duration-200 flex items-center justify-center gap-2 uppercase tracking-wider mt-1 sm:mt-2 disabled:opacity-50 active:scale-98"
          >
            {loading ? (
              <>
                <Loader2 size={15} className="animate-spin" />
                <span>Processando...</span>
              </>
            ) : mode === 'subscribe' ? (
              <>
                <span>Inscrever-se & Ativar Notificações</span>
                <ArrowRight size={13} />
              </>
            ) : (
              <>
                <span>Entrar na Minha Conta</span>
                <ArrowRight size={13} />
              </>
            )}
          </button>
        </form>

        {/* Switch Mode Footer */}
        <div className="mt-3.5 sm:mt-5 text-center font-sans text-xs text-stone-500">
          {mode === 'subscribe' ? (
            <p className="text-[11px] sm:text-xs">
              Já se inscreveu na nossa newsletter?{' '}
              <button
                type="button"
                onClick={() => { setMode('login'); setError(null); }}
                className="text-stone-300 hover:text-primary font-medium underline transition-colors"
              >
                Acessar minha conta
              </button>
            </p>
          ) : (
            <p className="text-[11px] sm:text-xs">
              Ainda não se inscreveu?{' '}
              <button
                type="button"
                onClick={() => { setMode('subscribe'); setError(null); }}
                className="text-stone-300 hover:text-primary font-medium underline transition-colors"
              >
                Cadastrar novo e-mail
              </button>
            </p>
          )}
        </div>

        {/* Privacy Note */}
        <p className="mt-2.5 sm:mt-4 text-[9px] sm:text-[10px] text-stone-600 text-center leading-relaxed font-sans">
          Ao se inscrever, você receberá notificações de novas publicações no Literafan e concorda com nossos Termos e Privacidade.
        </p>
      </div>
    </div>
  );
};

export default AuthModal;
